import React from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import UnlockUsersBaseModal from "./UnlockUsersBaseModal";

import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";
import { UserUnlockingStage } from "@/schemas/user/identity";
import { FIRST_STAGE_COMPLETION_SCORE } from "@/utils/unlockingStage";

type Props = {
  title: string;
  subtitle: string;
  currentUnlockingStage: UserUnlockingStage;
  progressBarTitle: string;
  earnedScore: number;
  totalScore: number;
  progressBarInfoComponent: React.JSX.Element;
  taskCardsComponent: React.JSX.Element;
  isModalVisible: boolean;
  onRequestClose: () => void;
  onRequestRefresh: () => void;
  isRefreshing: boolean;
};

function UnlockUsersFreeTierBaseModal({
  title,
  subtitle,
  currentUnlockingStage,
  progressBarTitle,
  earnedScore,
  totalScore,
  progressBarInfoComponent,
  taskCardsComponent,
  isModalVisible,
  onRequestClose,
  onRequestRefresh,
  isRefreshing,
}: Props): React.JSX.Element {
  const currentTheme = themes.morning;

  const styles = StyleSheet.create({
    headerCardContainer: {
      paddingVertical: Spacings.s2,
      paddingHorizontal: Spacings.s3,
      marginVertical: Spacings.s2,
      backgroundColor: "#f5f5f5",
      borderRadius: 8,
      borderWidth: 0.5,
      borderStyle: "solid",
      borderColor: currentTheme.borderColor,
    },
    aboveProgressBarTextContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingTop: Spacings.s1,
    },
    headerCardTitle: {
      fontSize: currentTheme.typography.h2.size.mobile - 3, // 15
      fontWeight: "bold",
    },
    score: {
      textAlign: "right",
    },
    progressBarContainer: {
      height: 10,
      backgroundColor: currentTheme.progressBarUnfilledColor,
      borderRadius: 5,
      overflow: "hidden",
      marginVertical: Spacings.s1,
    },
    progressBarStageOne: {
      height: "100%",
      backgroundColor: currentTheme.progressBarFilledColorStageOne,
      width: `${(Math.min(earnedScore, FIRST_STAGE_COMPLETION_SCORE) / totalScore) * 100}%`,
    },
    progressBarStageTwo: {
      height: "100%",
      backgroundColor: currentTheme.progressBarFilledColorStageTwo,
      width: `${((Math.min(earnedScore, totalScore) - FIRST_STAGE_COMPLETION_SCORE) / totalScore) * 100}%`,
    },
    sectionHeaderContainer: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    sectionHeader: {
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: "bold",
      marginTop: Spacings.s2,
      marginBottom: Spacings.s1,
    },
    refreshButton: {
      borderRadius: 20,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: Spacings.s3,
      height: 30,
      borderWidth: 0.5,
      borderColor: "black",
    },
    buttonText: {
      fontSize: currentTheme.typography.body.size.mobile,
    },
  });

  const modalBody: React.JSX.Element = (
    <>
      <Text>{subtitle}</Text>

      <View style={styles.headerCardContainer}>
        <View style={styles.aboveProgressBarTextContainer}>
          <Text style={styles.headerCardTitle}>{progressBarTitle}</Text>
          <Text style={[styles.headerCardTitle, styles.score]}>
            {earnedScore}/{totalScore} pts
          </Text>
        </View>
        <View style={styles.progressBarContainer}>
          <View style={{ flexDirection: "row", height: "100%" }}>
            <View style={styles.progressBarStageOne} />
            {currentUnlockingStage === UserUnlockingStage.ON_STAGE_TWO && (
              <View style={styles.progressBarStageTwo} />
            )}
          </View>
        </View>
        {progressBarInfoComponent}
      </View>

      <View>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionHeader}>Complete Tasks</Text>
          <Pressable
            style={styles.refreshButton}
            onPress={() => {
              onRequestRefresh();
            }}
          >
            <Text style={styles.buttonText}>
              {isRefreshing ? <ActivityIndicator size="small" /> : "Refresh"}
            </Text>
          </Pressable>
        </View>
        {taskCardsComponent}
      </View>
    </>
  );

  return (
    <UnlockUsersBaseModal
      title={title}
      modalBody={modalBody}
      isModalVisible={isModalVisible}
      onRequestClose={onRequestClose}
    />
  );
}

export default UnlockUsersFreeTierBaseModal;
